import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Building2, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useApp } from '@/presentation/hooks/use-app.js';
import { LOGO_MAX_SIZE_BYTES } from '@/shared/constants/client.js';

interface Props {
    logoUrl: string | null;
    logoFile: File | null;
    isEdit: boolean;
    onFileSelected: (file: File) => void;
    onRemoveFile: () => void;
    onUploadNow: () => void;
    onRemoveLogo: () => void;
    readOnly?: boolean;
}

export function ClientLogoPicker({ logoUrl, logoFile, isEdit, onFileSelected, onRemoveFile, onUploadNow, onRemoveLogo, readOnly = false }: Props) {
    const { translate } = useApp();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState('');

    const handleFile = (file: File) => {
        if (file.size > LOGO_MAX_SIZE_BYTES) {
            setError(translate('clients.logoTooLarge'));
            return;
        }
        setError('');
        onFileSelected(file);
    };

    const previewUrl = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : null), [logoFile]);
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <Box sx={{ width: 120 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Logo
            </Typography>
            {logoFile ? (
                <Stack direction="column" spacing={1} alignItems="center" sx={{ width: 120 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar src={previewUrl ?? undefined} variant="rounded" sx={{ width: 120, height: 120, borderRadius: 1.5 }} />
                        {!readOnly && (
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setError('');
                                    onRemoveFile();
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'error.dark' },
                                    width: 22,
                                    height: 22
                                }}
                            >
                                <X size={14} />
                            </IconButton>
                        )}
                    </Box>
                    <Typography variant="caption" noWrap sx={{ maxWidth: 120, fontWeight: 500 }}>
                        {logoFile.name}
                    </Typography>
                    {isEdit && !readOnly && (
                        <Button size="small" startIcon={<Upload size={12} />} onClick={onUploadNow}>
                            {translate('common.save')}
                        </Button>
                    )}
                </Stack>
            ) : logoUrl ? (
                <Stack direction="column" spacing={1} alignItems="center" sx={{ width: 120 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar src={logoUrl} variant="rounded" sx={{ width: 120, height: 120, borderRadius: 1.5 }} />
                        {!readOnly && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={onRemoveLogo}
                                sx={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'error.dark' },
                                    width: 22,
                                    height: 22
                                }}
                            >
                                <X size={14} />
                            </IconButton>
                        )}
                    </Box>
                    {!readOnly && (
                        <Button size="small" startIcon={<Upload size={12} />} sx={{ fontSize: 12 }} onClick={() => fileInputRef.current?.click()}>
                            {translate('clients.changeLogo')}
                        </Button>
                    )}
                </Stack>
            ) : (
                <Box
                    onClick={readOnly ? undefined : () => fileInputRef.current?.click()}
                    sx={{
                        alignItems: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        color: 'text.disabled',
                        cursor: readOnly ? 'default' : 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        height: 120,
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out',
                        width: 120,
                        '&:hover': readOnly ? undefined : { borderColor: 'primary.main', bgcolor: 'action.hover', color: 'primary.main' }
                    }}
                >
                    <Building2 size={22} />
                    <Typography variant="caption">PNG, JPG ou SVG</Typography>
                    <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
                        max. 2MB
                    </Typography>
                </Box>
            )}
            {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {error}
                </Typography>
            )}
            <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                        handleFile(f);
                    }
                    e.target.value = '';
                }}
            />
        </Box>
    );
}
